import { Highlighted } from '@starrez/starryui';
import { color, isEnter, isEscape, isSpace, Text } from '@starrez/starryui9';
import { GetCaptionFromViewType } from '../../../utils/stringUtil';
import React from 'react';
import styled from 'styled-components';
import { IMainDirectoryListItem } from '../../../graphql/useAllMainDirectoryListQuery';
import { strings } from '../../../localization';
import { useViewPicker } from '../context/ViewPickerContext';
import { Caption } from './ViewItem.styles';
import { ItemIcon, ViewItemIcon } from './ViewItemIcon';
import { ViewTypeIcon } from './ViewTypeIcon';

export interface IViewItemProps extends IMainDirectoryListItem {
    closeViewPicker?: boolean;
    searchString?: string;
    subItem?: boolean;
    unFavouritable?: boolean;
    showViewTypeIcon?: boolean;
}

export const ViewItem = ({ closeViewPicker, searchString, subItem, unFavouritable, showViewTypeIcon = false, ...props }: IViewItemProps) => {
    const { activeView, updateActiveView, updateFavouriteViews, closePicker } = useViewPicker();

    const handleUpdateView = () => {
        updateActiveView(props);
    };

    const handleUpdateFavourite = () => {
        updateFavouriteViews({ ...props });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (isEnter(e) || isSpace(e)) {
            handleUpdateView();
        } else if (closeViewPicker && isEscape(e)) {
            e.stopPropagation();
            closePicker();
        }
    };

    const isFavourite = props.favourite;
    const captionWithViewType = `${GetCaptionFromViewType(props.viewType)} ${props.caption}`;
    return (
        <Item role="option" aria-selected={activeView?.id === props.id} isFavourite={isFavourite}>
            <Caption
                type="button"
                aria-label={
                    isFavourite
                        ? (strings.formatString(strings.View_Label_ViewIsFavourite, captionWithViewType) as string)
                        : (strings.formatString(strings.View_Label_ViewIsUnfavourite, captionWithViewType) as string)
                }
                onClick={() => handleUpdateView()}
                onKeyDown={(e) => handleKeyDown(e)}
            >
                {showViewTypeIcon && <ViewTypeIcon viewType={props.viewType} />}
                <Text variant="normal.regular">
                    <Highlighted Text={props.caption} Highlight={searchString} />
                </Text>
            </Caption>
            {!unFavouritable && (
                <ViewItemIcon
                    ariaLabel={
                        isFavourite
                            ? (strings.formatString(strings.View_Label_ToggleFavouriteView, captionWithViewType) as string)
                            : (strings.formatString(strings.View_Label_ToggleUnfavouriteView, captionWithViewType) as string)
                    }
                    icon={isFavourite ? 'StarFilled' : 'StarOutline'}
                    onClick={() => handleUpdateFavourite()}
                    title={isFavourite ? strings.View_Label_ViewNotFavourited : strings.View_Label_ViewFavourited}
                    subItem={subItem}
                />
            )}
        </Item>
    );
};

interface IItemProps {
    isFavourite?: boolean;
    isFolder?: boolean;
}

export const Item = styled.li<IItemProps>`
    display: grid;
    align-items: center;
    grid-auto-columns: 1fr 36px;
    grid-auto-flow: column;
    min-height: 40px;
    cursor: pointer;
    border: 2px solid transparent;
    outline: 0;

    &:hover {
        background-color: ${color.Blue.B20};

        > ${Caption} {
            color: ${color.Blue.B60};
        }
    }

    ${ItemIcon},
    ${Caption} {
        background-color: transparent;
        border: 0;
        outline: 0;

        &:focus {
            box-shadow: 0 0 0 2px ${color.Blue.B80};
            border-radius: 4px;
        }
    }

    ${({ isFavourite, isFolder }) =>
        !isFavourite &&
        !isFolder &&
        `
        ${ItemIcon} {
            opacity: 0;
        }

        ${ItemIcon}:focus,
        &:hover > ${ItemIcon},
        ${Caption}:focus ~ ${ItemIcon} {
            opacity: 1;
        }
    `}
`;
